import Bugsnag from "@bugsnag/js";
import { YuLifeLocale } from "@hooks";
import { convertLocaleToRewardsRegion } from "@utils";
import { Reward, RewardCountry } from "components/pages/GlobalRewards/types";
import { RegionalReward } from "components/atoms/RewardCard";
import { getFallbackRewards } from "data/rewards";
import { Logger } from "services/logger";
import {
  sanitiseListOfMapRewardCountries,
  sanitiseMapRewards,
  sanitiseRewards,
} from "utils/rewards";

class ServerSideAPIRequestService {
  async getRewardProperties(locale: YuLifeLocale) {
    const rewardRegion = convertLocaleToRewardsRegion(locale);
    let rewardCountries = [] as RewardCountry[];
    let initialRewards = [] as Reward[];
    let regionalRewards = [] as RegionalReward[];

    try {
      const promises = await Promise.all([
        serverSideAPIRequestService.getCountriesWithRewards(),
        serverSideAPIRequestService.getRewardsForCountryMap(rewardRegion),
        serverSideAPIRequestService.getRewardsForRegion(rewardRegion, locale),
      ]);

      rewardCountries = sanitiseListOfMapRewardCountries(promises[0]);
      initialRewards = sanitiseMapRewards(promises[1], rewardRegion);
      regionalRewards = sanitiseRewards(promises[2], locale);

      if (!rewardCountries?.[0]) {
        regionalRewards = getFallbackRewards(locale);
      }
    } catch (e) {
      return {};
    }

    return {
      rewardCountries,
      initialRewards,
      regionalRewards,
      rewardRegion,
    };
  }

  async getCountriesWithRewards(): Promise<RewardCountry[]> {
    const url = `${process.env.API_URL}/website/rewards/map/countries/?verify=${process.env.WEBSITE_ROUTES_KEY}`;

    let data: { rewards: RewardCountry[] } = { rewards: [] };

    try {
      const response = await fetch(url);

      if (response.status !== 200) {
        throw new Error(`Rewards request unsuccessful: got status ${response.status}.`);
      }

      data.rewards = await response.json();

      if (!Array.isArray(data.rewards)) {
        throw new Error(
          `Rewards request unsuccessful: data is not an array. Got ${JSON.stringify(data.rewards)}`,
        );
      }
    } catch (e) {
      Logger.error(e);
      Bugsnag.notify(e);
      return [];
    }

    return data.rewards;
  }

  async getRewardsForRegion(region: string, requestedLocale: string): Promise<any> {
    const url = `${process.env.API_URL}/website/rewards?region=${region}&verify=${process.env.WEBSITE_ROUTES_KEY}&featured=1`;

    let data: { rewards: RegionalReward[] } = { rewards: [] };

    try {
      const response = await fetch(url);
      data.rewards = await response.json();
    } catch (e) {
      Bugsnag.notify(e);
      return [];
    }

    return data.rewards;
  }

  async getRewardsForCountryMap(countryId: string): Promise<Reward[]> {
    if (countryId === "US-main") {
      countryId = "US";
    }

    const url = `${process.env.API_URL}/website/rewards?region=${countryId}&verify=${process.env.WEBSITE_ROUTES_KEY}&featured=0`;

    let data = [];

    try {
      const response = await fetch(url);
      data = await response.json();
    } catch (e) {
      Bugsnag.notify(e);
      return [];
    }
    return data;
  }
}

export const serverSideAPIRequestService = new ServerSideAPIRequestService();
